import { BRAND } from './wedgeBrand';
import { useState } from 'react';
import { Alert, Button, TextField } from '@territory/ui';
import { API, apiUrl } from '@territory/client/routes';
import { useInAppBrowserGuard } from './wedgeInAppGuard';

/**
 * The EXTERNAL access-request form.
 *
 * 2026-05-28 Phase C-2/C-3 (login-experience rebuild) · the form now posts
 * directly to the canonical server-side `POST /api/auth/wedge-access/email-me`
 * endpoint. The endpoint mints a PENDING_APPROVAL user, derives the access
 * code via WEDGE_ACCESS_SECRET (stable, Phase B), and triggers a welcome-
 * with-code email via SES in one call. The old Web3Forms → manual-Gmail-
 * draft → manual-send loop is fully decommissioned for this surface.
 *
 * No session is issued here — the email-me endpoint queues a welcome email
 * with the access code. The visitor then enters that code in the redeem
 * screen to activate (which fires Phase 42's welcome-on-activation email).
 *
 * Shared by the pre-auth landing (WedgeLanding) and the rejected-login screen
 * (AccessDenied) so there is ONE request form. Rendered inside the parent's
 * branded glass card (caller supplies bg + brand mark) — this component owns
 * the form content only.
 *
 * See docs/wedge/specs/login-experience-rebuild.md.
 */

type FieldKey = 'name' | 'email' | 'company';
type FormView = 'form' | 'sent' | 'already_pending';

// Dedup helper. Before posting to Web3Forms we ask the wedge backend "do you
// already have a PENDING code on file for this email?". If yes we surface a
// "we already sent you a code, check your inbox" state instead of submitting
// a duplicate request. The endpoint NEVER leaks the code or whether the user
// exists in any other state, so this is safe to call from the browser. Best-
// effort: if the lookup itself fails we fall through to the normal submit
// path so a backend hiccup never blocks a real request.
async function checkAlreadyPending(email: string): Promise<{
  hasPendingCode: boolean;
  codeExpiresAt: string | null;
}> {
  try {
    const r = await fetch(
      `/api/auth/wedge-access/check-pending?email=${encodeURIComponent(email)}`,
      { method: 'GET', credentials: 'omit' },
    );
    if (!r.ok) return { hasPendingCode: false, codeExpiresAt: null };
    const j = (await r.json()) as {
      hasPendingCode?: boolean;
      codeExpiresAt?: string | null;
    };
    return {
      hasPendingCode: !!j.hasPendingCode,
      codeExpiresAt: j.codeExpiresAt ?? null,
    };
  } catch {
    return { hasPendingCode: false, codeExpiresAt: null };
  }
}

export function RequestAccessForm({ onBack }: { onBack: () => void }) {
  const [view, setView] = useState<FormView>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [focused, setFocused] = useState<FieldKey | null>(null);
  const inApp = useInAppBrowserGuard('request_access');

  const submit = async () => {
    setErr('');
    const n = name.trim();
    const e = email.trim();
    const co = company.trim();
    if (!n || !co || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setErr('Add your name, a valid work email, and your company.');
      return;
    }
    setBusy(true);
    try {
      // Dedup gate. Reese-style repeat submissions (we saw 7 from one email in
      // the launch-day inbox before the code stuck) get caught here BEFORE the
      // Web3Forms POST · the team never sees the duplicate notification email.
      const pending = await checkAlreadyPending(e);
      if (pending.hasPendingCode) {
        setView('already_pending');
        return;
      }
      const res = await fetch('/api/auth/wedge-access/email-me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ email: e, name: n, company: co }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(
          body.message ||
            'Could not send your request. Please try again in a moment.',
        );
      }
      setView('sent');
    } catch (ex) {
      setErr(
        ex instanceof Error ? ex.message : 'Could not send your request. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (view === 'sent') {
    return (
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 18,
            background: 'rgba(52,211,153,0.16)',
            color: '#34D399',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            border: '1px solid rgba(52,211,153,0.32)',
            boxShadow: '0 0 0 6px rgba(52,211,153,0.06)',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12.5l4.5 4.5L19 7.5"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10, letterSpacing: -0.3 }}>
          Check your inbox
        </div>
        <div style={{ fontSize: 13.5, opacity: 0.72, lineHeight: 1.6 }}>
          Thanks, {name.trim()}. We sent a one-tap sign-in link to{' '}
          <b style={{ color: BRAND.text }}>{email.trim()}</b>. You do not have to wait
          on it though, tap Continue with Google below to get in right now. If you
          need anything, email <b style={{ color: BRAND.text }}>luke@repvantage.io</b>.
        </div>
        {inApp.notice}
        <Button
          appearance="wedge-auth" authMotion="instant"
          onClick={() => inApp.guardGoogle(`${apiUrl(API.AUTH.GOOGLE)}?app=wedge`)} style={{ marginTop: 20 }}
        >
          Continue with Google
        </Button>
        <Button
          appearance="wedge-auth" variant="secondary" authSize="confirmation" authHover="quiet"
          onClick={onBack} style={{ width: 'auto', marginTop: 22 }}
        >
          Done
        </Button>
      </div>
    );
  }

  if (view === 'already_pending') {
    // Amber-toned dedup state · friendlier than red, signals "expected" not
    // "error".
    return (
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 18,
            background: 'rgba(245,158,11,0.16)',
            color: BRAND.amber,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            border: '1px solid rgba(245,158,11,0.32)',
            boxShadow: '0 0 0 6px rgba(245,158,11,0.06)',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 6h16M4 6l1.5 12a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2L20 6M9 11v5M15 11v5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10, letterSpacing: -0.3 }}>
          Check your inbox
        </div>
        {/* 2026-09-27 · Google-only copy rule: no access-code wording. The
            dedup gate above is unchanged; only the words are. */}
        <div style={{ fontSize: 13.5, color: '#CBD5E1', lineHeight: 1.6 }}>
          We already emailed{' '}
          <b style={{ color: '#fff' }}>{email.trim()}</b> a way in. Check your
          inbox (and spam folder), or skip the wait and use Google below.
        </div>
        {inApp.notice}
        <Button
          appearance="wedge-auth" authMotion="instant"
          onClick={() => inApp.guardGoogle(`${apiUrl(API.AUTH.GOOGLE)}?app=wedge`)} style={{ marginTop: 20 }}
        >
          Continue with Google
        </Button>
        <Button
          appearance="wedge-auth" variant="secondary" authSize="confirmation" authHover="quiet"
          onClick={onBack} style={{ width: 'auto', marginTop: 10 }}
        >
          Got it
        </Button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'left' }}>
      <div
        style={{
          fontSize: 19,
          fontWeight: 700,
          textAlign: 'center',
          marginBottom: 6,
          letterSpacing: -0.3,
        }}
      >
        Request PREP access
      </div>
      <div
        style={{
          fontSize: 13,
          opacity: 0.65,
          textAlign: 'center',
          lineHeight: 1.5,
          marginBottom: 18,
        }}
      >
        Tell us a bit about you. We'll email a one-tap PREP sign-in link to the address you enter below.
      </div>
      <TextField variant="wedge-auth"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onFocus={() => setFocused('name')}
        onBlur={() => setFocused(null)}
        placeholder="Your name"
        autoComplete="name"
        authFocused={focused === 'name'}
      />
      <TextField variant="wedge-auth"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onFocus={() => setFocused('email')}
        onBlur={() => setFocused(null)}
        placeholder="Work email"
        autoComplete="email"
        autoCapitalize="off"
        authFocused={focused === 'email'}
      />
      <TextField variant="wedge-auth"
        type="text"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        onFocus={() => setFocused('company')}
        onBlur={() => setFocused(null)}
        placeholder="Company"
        autoComplete="organization"
        authFocused={focused === 'company'}
      />
      {err && (
        <Alert variant="wedge-auth" authLook="form-error" style={{ margin: '2px 0 12px' }}>
          {err}
        </Alert>
      )}
      <Button appearance="wedge-auth" onClick={submit} disabled={busy}>
        {busy ? 'Sending request…' : 'Request access'}
      </Button>
      <Button
        appearance="wedge-auth" variant="ghost" authSize="form-back" authMotion="color"
        onClick={onBack}
        disabled={busy}
      >
        Back
      </Button>
    </div>
  );
}
